import { Component, Input, Output, EventEmitter, OnInit, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ScheduleException } from '../../../../models/schedule-exception';
import { ScheduleExceptionType } from '../../../../models/schedule-exception';
import { ScheduleDay } from '../../../../models/schedule';
import { toDateOnlyString } from '../../utils';
import { syncRangeEndWithStartMonth } from '../../date-range.utils';

@Component({
  selector: 'app-schedules-days-editor',
  standalone: true,
  templateUrl: './schedules-days-editor.component.html',
  styleUrls: ['./schedules-days-editor.component.css'],
  imports: [CommonModule, FormsModule],
})
export class SchedulesDaysEditorComponent implements OnInit {
  @Input({ required: true }) workingDays: ScheduleDay[] = [];
  @Input() canEditWorkingDays = true;
  @Input() invalidDayTitle?: string;
  @Input() context: any;
  @Input() isInvalidDay?: (context: any, day: string) => boolean;
  @Input() invalidDays: Set<string> | null = null;
  
  @Input({ required: true }) exceptions: ScheduleException[] = [];
  @Input() disabled = false;

  @Input() exceptionTypes: ScheduleExceptionType[] = [ 'Holiday', 'Vacation', 'TimeOff', 'Maintenance', 'Other' ];

  @Output() workingDaysChange = new EventEmitter<ScheduleDay[]>();
  @Output() exceptionsChange = new EventEmitter<ScheduleException[]>();

  trackByIndex = (i: number) => i;

  ngOnInit(): void {
    
  }

  onWorkingDayChanged() {
    this.workingDaysChange.emit(this.workingDays);
  }

  toggleDay(day: string, event: any) {
    const checked = event.target.checked;
    const hour = this.workingDays.find((h: any) => h.day === day);
    if (hour) {
      hour.isActive = checked;

      if (checked && day !== "Monday") {
        const monday = this.workingDays.find((h: any) => h.day === "Monday");
        if (monday && monday.isActive) {
          hour.startTime = monday.startTime;
          hour.endTime = monday.endTime;
        }
      }
    }
  }

  addException() {
    const ex: ScheduleException = {
      type: 'Vacation',
      startDate: this.today(),
      endDate: this.today(),
      repeatYearly: false,
      description: ''
    };

    this.exceptions.push(ex);
    this.revalidateAll();
    this.emit();
  }

  removeException(index: number) {
    this.exceptions.splice(index, 1);
    this.revalidateAll();
    this.emit();
  }

  onStartDateChange(ex: ScheduleException, startDate: string) {
    const previousStart = ex.startDate;
    ex.endDate = syncRangeEndWithStartMonth(previousStart, ex.endDate, startDate) ?? startDate;
    ex.startDate = startDate;
    this.onDateChange(ex);
  }

  onDateChange(ex: ScheduleException) {
    this.validateException(ex);
    this.revalidateAllOverlaps();
    this.emit();
  }

  validateException(ex: ScheduleException) {
    ex._invalid = !!(ex.startDate && ex.endDate && ex.endDate < ex.startDate);
  }

  hasInvalidExceptions(): boolean {
    return this.exceptions?.some(x => x._invalid) ?? false;
  }

  // --- overlap logic ---
  revalidateAll() {
    for (const ex of this.exceptions) this.validateException(ex);
    this.revalidateAllOverlaps();
  }

  private revalidateAllOverlaps() {
    // reset
    this.exceptions.forEach(e => (e._overlaps = []));

    // overlaps считаем только если даты валидные
    const norm = (e: ScheduleException) => ({
      start: e.startDate,
      end: e.endDate,
      repeat: e.repeatYearly
    });

    for (let i = 0; i < this.exceptions.length; i++) {
      const a = this.exceptions[i];
      if (a._invalid) continue;

      for (let j = i + 1; j < this.exceptions.length; j++) {
        const b = this.exceptions[j];
        if (b._invalid) continue;

        if (this.overlaps(norm(a), norm(b))) {
          a._overlaps!.push(j);
          b._overlaps!.push(i);
        }
      }
    }
  }

  private overlaps(
    a: { start: string; end: string; repeat: boolean },
    b: { start: string; end: string; repeat: boolean }
  ): boolean {
    if (!a.repeat && !b.repeat) {
      return !(a.end < b.start || b.end < a.start);
    }

    const aStart = a.start.slice(5); // 'MM-DD'
    const aEnd = a.end.slice(5);
    const bStart = b.start.slice(5);
    const bEnd = b.end.slice(5);

    const inRange = (x: string, s: string, e: string) => {
      // поддержка диапазона через Новый год (например 12-28..01-03)
      if (s <= e) return x >= s && x <= e;
      return x >= s || x <= e;
    };

    // проверим любое пересечение через "точки"
    return (
      inRange(aStart, bStart, bEnd) ||
      inRange(aEnd, bStart, bEnd) ||
      inRange(bStart, aStart, aEnd) ||
      inRange(bEnd, aStart, aEnd)
    );
  }

  addToday() {
    const t = this.today();
    this.exceptions.push({ type: 'Holiday', startDate: t, endDate: t, repeatYearly: false, description: '' });
    this.revalidateAll();
    this.emit();
  }

  addWeekend() {
    // простая версия: добавим ближайшие суб/вс
    const d = new Date();
    const day = d.getDay(); // 0 Sun .. 6 Sat
    const daysUntilSat = (6 - day + 7) % 7;
    const sat = new Date(d);
    sat.setDate(d.getDate() + daysUntilSat);

    const sun = new Date(sat);
    sun.setDate(sat.getDate() + 1);

    this.exceptions.push({
      type: 'Vacation',
      startDate: toDateOnlyString(sat),
      endDate: toDateOnlyString(sun),
      repeatYearly: false,
      description: 'Weekend'
    });

    this.revalidateAll();
    this.emit();
  }

  addRange(days: number) {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + Math.max(0, days - 1));

    this.exceptions.push({
      type: 'Vacation',
      startDate: toDateOnlyString(start),
      endDate: toDateOnlyString(end),
      repeatYearly: false,
      description: `${days}-day vacation`
    });

    this.revalidateAll();
    this.emit();
  }

  private today(): string {
    return toDateOnlyString(new Date());
  }

  emit() {
    this.exceptionsChange.emit(this.exceptions);
  }
}
